import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_ORIGIN } from '../api/client';

// 텍스트 로고 — 첫 단어를 파란색(.login-logo span CSS)으로 강조하고 나머지는 일반 색.
function TextLogo({ text }) {
  const idx = text.indexOf(' ');
  if (idx < 0) return <span>{text}</span>;
  return <><span>{text.slice(0, idx)}</span>{text.slice(idx)}</>;
}

function GhostSuffixInput({ value, onChange, suffix, ...props }) {
  const mirrorRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);
  const showGhost = !value.includes('@');
  useLayoutEffect(() => {
    if (mirrorRef.current) setTextWidth(mirrorRef.current.offsetWidth);
  }, [value]);
  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" value={value} onChange={onChange} {...props} />
      <span ref={mirrorRef} aria-hidden="true" style={{
        position: 'absolute', left: 0, top: 0, visibility: 'hidden',
        whiteSpace: 'pre', padding: '9px 12px',
        fontSize: 13, fontFamily: 'inherit',
      }}>{value}</span>
      {showGhost && (
        <span aria-hidden="true" style={{
          position: 'absolute',
          left: `${textWidth}px`,
          top: '50%', transform: 'translateY(-50%)',
          color: '#cbd5e1', pointerEvents: 'none',
          fontSize: 13, fontFamily: 'inherit',
          whiteSpace: 'pre',
        }}>{suffix}</span>
      )}
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 세션 만료로 강제 로그아웃된 경우 안내 메시지
  const expired = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('expired') === '1';
  const [error, setError] = useState(expired ? '세션이 만료되었습니다. 다시 로그인해 주세요.' : '');
  const [loading, setLoading] = useState(false);
  // 사이트 관리에서 설정한 로그인 화면 로고/부제
  const [site, setSite] = useState({
    login_logo_type: 'text',
    login_logo_text: 'SOOSAN ERP',
    login_logo_url: null,
    login_subtitle: '수산 견적 관리 시스템',
  });
  useEffect(() => {
    let mounted = true;
    fetch(`${API_ORIGIN}/api/site-info`).then(r => r.ok ? r.json() : null).then(d => {
      if (mounted && d) setSite(s => ({ ...s, ...d }));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const finalEmail = email.includes('@') ? email : `${email}@soosan.co.kr`;
    setError('');
    setLoading(true);
    try {
      await login(finalEmail, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          {site.login_logo_type === 'image' && site.login_logo_url
            ? <img src={site.login_logo_url} alt="로고" style={{maxHeight:48,maxWidth:'100%'}} />
            : <TextLogo text={site.login_logo_text || 'SOOSAN ERP'} />}
        </div>
        <div className="login-subtitle">{site.login_subtitle || '수산 견적 관리 시스템'}</div>
        {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label className="form-label">이메일</label>
            <GhostSuffixInput
              type="text"
              value={email}
              onChange={e => setEmail(e.target.value)}
              suffix="@soosan.co.kr"
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label className="form-label">비밀번호</label>
            <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="비밀번호를 입력하세요" autoComplete="current-password" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" /> 로그인 유지
            </label>
            <span style={{ fontSize: 13, color: '#3b82f6', cursor: 'pointer' }}>비밀번호 찾기</span>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}
            style={{ width: '100%', padding: 12, fontSize: 15, borderRadius: 10, opacity: loading ? 0.7 : 1 }}>
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>
      </div>
    </div>
  );
}
